import type { ReactElement, ReactNode } from 'react'
import {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useMediaQuery } from 'usehooks-ts'

import { CarouselContext } from './CarouselContext'
import { CarouselDots } from './components/CarouselDots'
import {
  CarouselItem,
  type ICarouselItemProps,
} from './components/CarouselItem'
import { useCarouselControl } from './hooks/useCarouselControl'
import { CarouselStackStyled } from './styles/CarouselStackStyled'
import { CarouselStyled } from './styles/CarouselStyled'
import { Space } from '@ui/elements/Space'

export interface ICarouselProps {
  children: ReactNode
  /** Controls the active slide. Omit to let the carousel manage its own state. */
  activeIndex?: number
  onActiveIndexChange?: (index: number) => void
  /** Delay between auto-cycles in ms. `null` disables auto-cycling. */
  interval?: number | null
}

const isCarouselItem = (
  child: ReactNode
): child is ReactElement<ICarouselItemProps> =>
  isValidElement(child) && child.type === CarouselItem

export const Carousel = ({
  children,
  activeIndex: activeIndexProp,
  onActiveIndexChange,
  interval = null,
}: ICarouselProps) => {
  const { activeIndex, setActiveIndex, isControlled } = useCarouselControl({
    activeIndexProp,
    onActiveIndexChangeProp: onActiveIndexChange,
  })

  const [isPaused, setIsPaused] = useState(false)
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

  const { items, controls } = useMemo(() => {
    const itemElements: ReactNode[] = []
    const controlElements: ReactNode[] = []

    Children.forEach(children, (child) => {
      if (isCarouselItem(child)) {
        itemElements.push(cloneElement(child, { index: itemElements.length }))
      } else {
        controlElements.push(child)
      }
    })

    return { items: itemElements, controls: controlElements }
  }, [children])

  const itemCount = items.length

  useEffect(() => {
    if (!isControlled && activeIndex > itemCount - 1) {
      setActiveIndex(0)
    }
  }, [activeIndex, isControlled, itemCount, setActiveIndex])

  const remainingRef = useRef(interval ?? 0)
  const startedAtRef = useRef(0)

  // A slide change (auto-advance or manual) always restarts a full interval.
  useEffect(() => {
    remainingRef.current = interval ?? 0
  }, [activeIndex, interval])

  useEffect(() => {
    if (
      interval === null ||
      itemCount < 2 ||
      isPaused ||
      prefersReducedMotion
    ) {
      // Pausing banks the time left so resuming continues the countdown
      // instead of restarting a full interval.
      if (isPaused && startedAtRef.current !== 0) {
        remainingRef.current = Math.max(
          0,
          remainingRef.current - (Date.now() - startedAtRef.current)
        )
        startedAtRef.current = 0
      }

      return
    }

    startedAtRef.current = Date.now()
    const timer = setTimeout(() => {
      remainingRef.current = interval
      setActiveIndex((activeIndex + 1) % itemCount)
    }, remainingRef.current)

    return () => clearTimeout(timer)
  }, [
    activeIndex,
    interval,
    isPaused,
    itemCount,
    prefersReducedMotion,
    setActiveIndex,
  ])

  const contextValue = useMemo(
    () => ({ activeIndex, setActiveIndex, itemCount }),
    [activeIndex, setActiveIndex, itemCount]
  )

  return (
    <CarouselContext.Provider value={contextValue}>
      <CarouselStyled
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <Space size='large'>
          <CarouselStackStyled>{items}</CarouselStackStyled>

          {controls}
        </Space>
      </CarouselStyled>
    </CarouselContext.Provider>
  )
}

Carousel.Item = CarouselItem
Carousel.Dots = CarouselDots
